import {
  Background,
  Node,
  Panel,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Card, darken, lighten, useTheme } from '@mui/material'
import '@xyflow/react/dist/style.css'
import { Document } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import useProjectFieldInfos from '@/hooks/useProjectFieldInfos'
import useGetValidationDocDataQueries from '@/services/hooks/useGetValidationDocDataQueries'
import { getCanvasPositionX, getZoomScale } from '@/utils/image-zoom-pan'
import ImageZoomPanControls from '@/components/image-zoom-pan/ImageZoomPanControls'
import { useDocumentImagePageContext } from '@/components/image-zoom-pan/providers/DocumentImagePageProvider'
import useDocumentValidationSubscribe from '@/components/notifications/useDocumentValidationSubscribe'
import { useContainerSize } from '@/components/size-provider/SizeProvider'
import FieldGrid from '@/components/validation/FieldGrid'
import ValidationFieldsPanel from '@/components/validation/ValidationFieldsPanel'
import DocumentImageNode, {
  CARD_HEIGHT,
  CARD_WIDTH,
} from './idle-components/DocumentImageNode'
import UpdateStatusCard from './UpdateStatusCard'

type DocumentDisplayProps = {
  document: Document
  dataQueries: ReturnType<typeof useGetValidationDocDataQueries>
  isProcessing: boolean
}

const nodeTypes = {
  imageType: DocumentImageNode,
}

export default function DocumentDisplay({
  document,
  dataQueries,
  isProcessing,
}: DocumentDisplayProps) {
  const theme = useTheme()
  const fitOnLoadRef = useRef(false)
  const reactFlowInstance = useReactFlow()
  const [rotationDegree, setRotationDegree] = useState(0)
  const { imagePage } = useDocumentImagePageContext()

  const [fieldsContainerEl, setFieldsContainerEl] =
    useState<HTMLDivElement | null>(null)
  const [fieldsContainerWidth, setFieldsContainerWidth] = useState(
    fieldsContainerEl?.clientWidth || 0,
  )
  const { width, height } = useContainerSize()

  const initialNodes = useMemo(
    () => [
      {
        id: 'image',
        type: 'imageType',
        data: {
          documentPage: document.document_pages[imagePage - 1],
          rotationDegree,
          visible: !document.document_pages.length,
        },
        draggable: false,
        selectable: false,
        position: { x: 0, y: 0 },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pages, for some reason, are not referencing the same object when they should
    [document.id, imagePage, rotationDegree],
  )

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes as Node[])
  const [edges, , onEdgesChange] = useEdgesState([])
  const { fieldInfos } = useProjectFieldInfos()

  function handleRotate() {
    setRotationDegree((prev) => (prev + 90 === 360 ? 0 : prev + 90))
  }

  const groupedFields = useMemo(() => {
    return fieldInfos.reduce<ProjectGridField[][]>((acc, fieldInfo) => {
      if (fieldInfo.fields) {
        acc.push([{ ...fieldInfo.field, fields: fieldInfo.fields }])
        acc.push([])
      } else {
        if (acc.length === 0) {
          acc.push([])
        }
        acc[acc.length - 1].push(fieldInfo.field)
      }

      return acc
    }, [])
  }, [fieldInfos])

  const imageWidth = useMemo(
    () => document.document_pages[imagePage - 1]?.width || CARD_WIDTH,
    [document.document_pages, imagePage],
  )

  const imageHeight = useMemo(
    () => document.document_pages[imagePage - 1]?.height || CARD_HEIGHT,
    [document.document_pages, imagePage],
  )

  // zoonScale is how much you have to zoom in or out to fit the image in the screen, with 1 being not zoomed at all
  const zoomScale = useMemo(() => {
    return getZoomScale({
      imageSize: {
        width: imageWidth,
        height: imageHeight,
      },
      containerSize: { width, height },
      fieldsContainerWidth,
    })
  }, [fieldsContainerWidth, height, imageHeight, imageWidth, width])

  const centerOnDoc = useCallback(
    (duration = 200, timeout = 0) => {
      if (isProcessing) {
        setTimeout(() => {
          reactFlowInstance.fitView({
            padding: 0.15,
            duration,
          })
        }, timeout)
      } else {
        reactFlowInstance.setViewport(
          {
            x: getCanvasPositionX({
              imageWidth,
              fieldsContainerWidth,
              paneWidth: width,
              zoomScale,
            }),
            y: 0,
            zoom: zoomScale,
          },
          {
            duration,
          },
        )
      }
    },
    [
      fieldsContainerWidth,
      imageWidth,
      isProcessing,
      reactFlowInstance,
      width,
      zoomScale,
    ],
  )

  useEffect(() => {
    if (!fieldsContainerEl) {
      setFieldsContainerWidth(0)
    } else {
      const observer = new ResizeObserver(() => {
        setFieldsContainerWidth(fieldsContainerEl.clientWidth)
      })
      observer.observe(fieldsContainerEl)
      return () => {
        observer.disconnect()
      }
    }
  }, [fieldsContainerEl, setFieldsContainerWidth])

  useEffect(() => {
    // Reset fit on load when the image page changes
    fitOnLoadRef.current = false
    setNodes((prev) => [
      {
        ...prev[0],
        data: {
          ...initialNodes[0].data,
        },
      },
    ])
  }, [initialNodes, setNodes])

  useEffect(() => {
    if (fitOnLoadRef.current || (!isProcessing && !fieldsContainerWidth)) return
    fitOnLoadRef.current = true
    centerOnDoc(100, 100)
    setNodes((prev) => [
      {
        ...prev[0],
        data: {
          ...initialNodes[0].data,
          visible: true,
        },
      },
    ])
  }, [centerOnDoc, fieldsContainerWidth, initialNodes, isProcessing, setNodes])

  useDocumentValidationSubscribe({
    documentId: document?.id,
    documentQueryKey: dataQueries.documentQuery.queryKey,
    chipsListQueryKey: dataQueries.documentChipsQuery.queryKey,
    rowsListQueryKey: dataQueries.documentRowsQuery.queryKey,
    rowValuesListQueryKey: dataQueries.documentRowValuesQuery.queryKey,
  })
  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        height: '100%',
        '& svg': {
          overflow: 'visible !important', // Something in MUI is messing with this, so this is a hack to fix it.
        },
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        style={{
          background: theme.palette.background.default,
        }}
        minZoom={0.05}
        fitView
      >
        <Background
          color={
            theme.palette.mode === 'dark'
              ? lighten(theme.palette.background.paper, 0.1)
              : darken(theme.palette.background.paper, 0.2)
          }
          size={2}
        />
        {!isProcessing && (
          <Panel position="top-right" style={{ height: '100%', margin: 0 }}>
            <ValidationFieldsPanel
              layout="right"
              setFieldsContainerEl={setFieldsContainerEl}
              isLite
            >
              {groupedFields.map((group) => {
                if (group.length === 0) return null
                const isTableField = group[0].fields
                return (
                  <Card
                    key={group[0].id}
                    elevation={4}
                    sx={{
                      p: isTableField ? 0 : 1,
                      pointerEvents: 'all',
                    }}
                  >
                    <FieldGrid document={document} fields={group} />
                  </Card>
                )
              })}
            </ValidationFieldsPanel>
          </Panel>
        )}
        <Panel position="bottom-left">
          <Card
            elevation={4}
            sx={{
              borderRadius: 8,
              padding: 0.5,
            }}
          >
            <ImageZoomPanControls
              documentView={isProcessing ? 'lite-processing' : 'lite-idle'}
              hidePaginationControls={
                !document?.document_pages?.length ||
                document?.document_pages?.length <= 1
              }
              onCenterImage={() => {
                centerOnDoc()
              }}
              rotateImage={handleRotate}
            />
          </Card>
        </Panel>
        {isProcessing && document.workflow_states_ids && (
          <Panel
            position="top-left"
            style={{
              overflowY: 'auto',
              maxHeight: 'calc(100% - 80px)',
            }}
          >
            <UpdateStatusCard stateIds={document.workflow_states_ids} />
          </Panel>
        )}
      </ReactFlow>
    </Box>
  )
}
